import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import "@/styles/hs-auth.css";
import { isHealthSource } from "@/lib/brand";

type BrandedAuthShellProps = {
  children: ReactNode;
};

/**
 * Shared layout for login, task intake, and other auth flows. Styles are scoped under
 * `.hs-auth-shell` in `hs-auth.css`.
 *
 * HealthSource gets the split hero layout; every other practice gets a plain header bar and a
 * centered form (like patientadmin.com), with no vendor branding: patients don't know us.
 */
export function BrandedAuthShell({ children }: BrandedAuthShellProps) {
  if (!isHealthSource()) return <PlainAuthShell>{children}</PlainAuthShell>;
  return <HealthSourceAuthShell>{children}</HealthSourceAuthShell>;
}

function PlainAuthShell({ children }: BrandedAuthShellProps) {
  return (
    <div className="hs-auth-shell flex min-h-dvh flex-col bg-white">
      <header
        className="h-[calc(3.75rem+env(safe-area-inset-top,0px))] shrink-0 bg-healthsource-chrome pt-[env(safe-area-inset-top,0px)]"
        aria-hidden="true"
      />
      <main className="flex flex-1 justify-center pb-[max(2.5rem,env(safe-area-inset-bottom,0px))] pl-[max(1rem,env(safe-area-inset-left,0px))] pr-[max(1rem,env(safe-area-inset-right,0px))] pt-10 sm:px-6 md:pt-16">
        <div className="w-full max-w-[420px]">{children}</div>
      </main>
    </div>
  );
}

function HealthSourceAuthShell({ children }: BrandedAuthShellProps) {
  const year = new Date().getFullYear();

  return (
    <div className="hs-auth-shell min-h-dvh grid grid-cols-1 md:grid-cols-[1.05fr_1fr] bg-white">
      <aside
        className="hs-auth-brand relative hidden md:flex min-h-dvh overflow-hidden"
        style={{
          backgroundImage: "url('/hs/hs-front-desk.webp')",
          backgroundSize: "cover",
          backgroundPosition: "right center",
        }}
        aria-hidden="true"
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(115deg, rgba(30,37,43,0.78) 0%, rgba(42,64,100,0.58) 48%, rgba(42,64,100,0.15) 100%)",
          }}
        />
        <div className="relative z-10 flex h-full w-full flex-col justify-between p-14 text-white">
          <LogoLockup size={52} />
          <div className="max-w-[460px]">
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#F97AA1",
                marginBottom: 18,
              }}
            >
              Patient Portal
            </div>
            <h1
              style={{
                fontSize: 60,
                fontWeight: 900,
                letterSpacing: "-0.025em",
                lineHeight: 1.02,
                color: "#fff",
                margin: 0,
              }}
            >
              Live Life.
              <br />
              <span style={{ color: "var(--hs-red)" }}>Pain-Free.</span>
            </h1>
            <p
              style={{
                fontSize: 17,
                lineHeight: 1.6,
                marginTop: 20,
                maxWidth: 420,
                color: "rgba(255,255,255,0.92)",
                fontWeight: 400,
              }}
            >
              Provide the clinic information, book appointments, and message your care
              team — all in one place.
            </p>
          </div>
          <div
            style={{ fontSize: 12, opacity: 0.85 }}
            className="flex flex-wrap items-center gap-x-2 gap-y-1"
          >
            <span>
              Copyright {year} &copy; HealthCORE Technology, LLC. All rights reserved.
            </span>
            <span aria-hidden>·</span>
            <Link to="/contact" aria-label="Contact us">
              Contact Us
            </Link>
            <span aria-hidden>·</span>
            <span
              title="Coming soon"
              aria-label="Terms of use (coming soon)"
              style={{ cursor: "default" }}
            >
              Terms
            </span>
            <span aria-hidden>·</span>
            <span
              title="Coming soon"
              aria-label="Privacy policy (coming soon)"
              style={{ cursor: "default" }}
            >
              Privacy
            </span>
          </div>
        </div>
      </aside>

      <main className="flex items-center justify-center bg-white pb-[max(2.5rem,env(safe-area-inset-bottom,0px))] pl-[max(1rem,env(safe-area-inset-left,0px))] pr-[max(1rem,env(safe-area-inset-right,0px))] pt-[max(2.5rem,env(safe-area-inset-top,0px))] sm:px-6 md:px-14">
        <div className="w-full max-w-[420px]">{children}</div>
      </main>
    </div>
  );
}

function LogoLockup({ size = 52 }: { size?: number }) {
  const accent = "var(--hs-red)";
  return (
    <div
      className="inline-flex items-center"
      style={{ gap: 14, fontFamily: "inherit" }}
    >
      <div
        style={{
          background: accent,
          color: "#fff",
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 900,
          fontSize: size * 0.5,
          letterSpacing: "-0.03em",
          lineHeight: 1,
          borderRadius: 2,
          flexShrink: 0,
        }}
      >
        HS
      </div>
      <div style={{ lineHeight: 1.05 }}>
        <div
          style={{
            fontWeight: 800,
            color: "#fff",
            fontSize: size * 0.42,
            letterSpacing: "-0.015em",
          }}
        >
          HealthSource
        </div>
        <div
          style={{
            color: accent,
            fontSize: size * 0.22,
            fontWeight: 700,
            marginTop: 2,
            letterSpacing: "0.01em",
          }}
        >
          America&apos;s Chiropractor
        </div>
      </div>
    </div>
  );
}
